import React, { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../hooks/AuthProvider';
import WysiwygEditor from '../editor/WysiwygEditor';
import EmailTagsInput from '../inputs/EmailTagsInput';
import VenueSelector from '../admin/VenueSelector';
import ExcelImportModal, { ImportOption } from './ExcelImportModal';
import { parseExcelSchedule } from './excelUtils';
import '../../styles/ConferenceManager.css';
import { cleanHtml } from '../htmlCleaner';

import { ClientFirebaseConfig, clientStorageService } from '../client/clientStorageService';

// Standard icons based on the mock data
const DEFAULT_ICONS: Icon[] = [
  { id: "welcome", name: "Welcome", enabled: true, HTML: '<div><b style="font-family: -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, Oxygen, Ubuntu, Cantarell, &quot;Open Sans&quot;, &quot;Helvetica Neue&quot;, sans-serif; font-size: 0.875rem;">Welcome to the Conference!</b></div><div><b><br></b></div><div><b><br></b></div><div><b><br></b></div><div><b><br></b></div><div><b><br></b></div><div><b>Christina McAnea</b></div><div><b><br></b></div><div><img src="https://convention.afscme.org/about/keynote-speakers/headshot/Christina_McAnea.jpg" width="30%" style="--tw-scale-x: 1; --tw-scale-y: 1; --tw-pan-x: ; --tw-pan-y: ; --tw-pinch-zoom: ; --tw-scroll-snap-strictness: proximity; --tw-gradient-from-position: ; --tw-gradient-via-position: ; --tw-gradient-to-position: ; --tw-ordinal: ; --tw-slashed-zero: ; --tw-numeric-figure: ; --tw-numeric-spacing: ; --tw-numeric-fraction: ; --tw-ring-inset: ; --tw-ring-offset-width: 0px; --tw-ring-offset-color: #fff; --tw-ring-color: rgb(59 130 246 / 0.5); --tw-ring-offset-shadow: 0 0 #0000; --tw-ring-shadow: 0 0 #0000; --tw-shadow: 0 0 #0000; --tw-shadow-colored: 0 0 #0000; --tw-blur: ; --tw-brightness: ; --tw-contrast: ; --tw-grayscale: ; --tw-hue-rotate: ; --tw-invert: ; --tw-saturate: ; --tw-sepia: ; --tw-drop-shadow: ; --tw-backdrop-blur: ; --tw-backdrop-brightness: ; --tw-backdrop-contrast: ; --tw-backdrop-grayscale: ; --tw-backdrop-hue-rotate: ; --tw-backdrop-invert: ; --tw-backdrop-opacity: ; --tw-backdrop-saturate: ; --tw-backdrop-sepia: ; --tw-contain-size: ; --tw-contain-layout: ; --tw-contain-paint: ; --tw-contain-style: ;"></div><div><i><br></i></div><div><i>General Secretary</i></div>' },
  { id: "venue-details", name: "Venue Details", enabled: true, HTML: "<b>Venue Details</b>" },
  { id: "health-safety", name: "Health & Safety", enabled: true, HTML: "<b>Health & Safety</b>" },
  { id: "conf-docs", name: "Conf Docs", enabled: true, HTML: "<b>Conference Docs</b>" },
  { id: "soc-reports", name: "SOC Reports", enabled: true, HTML: "<b>SOC Reports</b>" },
  { id: "fringes-timetable", name: "Fringes Timetable", enabled: true, HTML: "<b>Fringes Timetable</b>" },
  { id: "working-together", name: "Working Together", enabled: true, HTML: "<b>Working Together</b>" },
  { id: "other-meetings", name: "Other Meetings", enabled: true, HTML: "<b>Other Meetings</b>" },
  { id: "newssheets", name: "UNISON Newssheets", enabled: true, HTML: "<b>UNISON Newssheets</b>" },
  { id: "attendees-chat", name: "Attendees Chat", enabled: true, HTML: "<b>Attendees Chat</b>" },
  { id: "exhibitors", name: "UNISON Exhibitors", enabled: true, HTML: "<b>UNISON Exhibitors</b>" },
  { id: "unizone", name: "Unizone", enabled: true, HTML: "<b>Unizone</b>" },
  { id: "year-of-theme", name: "Year of [Theme]", enabled: true, HTML: "<b>Year of [Theme]</b>" },
  { id: "discounts", name: "Discounts", enabled: true, HTML: "<b>Discounts</b>" },
  { id: "fair-representation", name: "Fair Representation", enabled: true, HTML: "<b>Fair Representation</b>" },
  { id: "solidarity-stands", name: "Solidarity Stands", enabled: true, HTML: "<b>Solidarity Stands</b>" },
  { id: "ocs-howto", name: "OCS How-To", enabled: true, HTML: "<b>OCS How-To</b>" },
  { id: "follow-us", name: "Follow Us", enabled: true, HTML: '<a href="https://www.jotlu.com/links/unison-conferences/">Follow Us</a>', externalSite: 'https://www.jotlu.com/links/unison-conferences/'},
  { id: "future-conferences", name: "Future Conferences", enabled: true, HTML: '<b>Future Conferences</b>'}
];

// Define interfaces for schedule events
interface ScheduleEvent {
  id: string;
  title: string;
  startTime: string; // Format: HH:MM
  endTime: string;   // Format: HH:MM
  location: string;
  date: string;      // Format: YYYY-MM-DD
  description?: string;
  type?: string;     // For color coding (e.g., "break", "workshop", "ceremony")
}

interface LinkItem {
  id?: string;
  name: string;
  url?: string;
  thumbnailUrl?: string;
  nonExternalMode?: boolean;
  description?: string;
  availability?: string;
  appointmentsRequired?: boolean;
  promotions?: string;
  otherInfo?: string;
}

interface Icon {
  id: string;
  name: string;
  enabled: boolean;
  HTML: string;
  iconConfig?: {
    type: 'links';
    groups: Array<{
      groupName: string;
      links: LinkItem[];
    }>;
  };
  externalSite?: string;
}

interface ConferenceData {
  name: string;
  data: {
    id?: string;
    enabled?: string | boolean;
    name?: string;
    startDate?: any; // Using any to handle various date formats
    endDate?: any; // Using any to handle various date formats
    venue?: any;
    icons?: Icon[];
    schedule?: ScheduleEvent[];
    moderators?: string[]; // Array of moderator emails
    bannedChatUsers?: string[]; // Array of banned user emails
    chatDisclaimer?: string; // Text for chat disclaimer
    featuredImageUrl?: string; // URL for home screen image
    [key: string]: any;
  };
  lastUpdated: number;
}

interface MetadataField {
  id: string;
  name: string;
  enabled?: boolean;
}

interface MetadataCollection {
  permissions: {
    read: string | string[];
    write: string | string[];
  };
  fields: MetadataField[];
}

interface ConferenceManagerProps {
  db: Firestore;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  clientStorageConfig?: ClientFirebaseConfig; // Add this optional prop
}

// Type guard for Firestore Timestamp
function isFirestoreTimestamp(value: any): boolean {
  return value && 
    typeof value === 'object' && 
    (
      // Check for toDate method (Firestore SDK object)
      (typeof value.toDate === 'function') ||
      // Or check for seconds and nanoseconds (serialized timestamp from cache)
      ('seconds' in value && 'nanoseconds' in value)
    );
}

const ConferenceManager: React.FC<ConferenceManagerProps> = ({ 
  db, 
  functions, 
  onError, 
  onSuccess, 
  setLoading,
  clientStorageConfig
}) => {
  const { currentUser } = useAuth();
  const [conferences, setConferences] = useState<MetadataField[]>([]);
  const [selectedConference, setSelectedConference] = useState<string | null>(null);
  const [conferenceData, setConferenceData] = useState<ConferenceData | null>(null);
  const [formData, setFormData] = useState<{
    enabled: string;
    name: string;
    startDate: string;
    endDate: string;
    venue: string;
    venueAddress: string;
    venueDetails: {
      refreshments: boolean;
      refreshmentsDetails: string;
      wifi: boolean;
      wifiNetwork: string;
      wifiPassword: string;
      accessibility: boolean;
      accessibilityDetails: string;
      additionalInfo: string;
    };
    moderators: string[];
    bannedChatUsers: string[]; 
    chatDisclaimer: string;
    chatEnabled: boolean; // Added for chat enabled status
    moderatorOnlyChat: boolean; // Added for moderator-only chat mode
    featuredImageUrl: string;
  }>({
    enabled: 'true',
    name: '',
    startDate: '',
    endDate: '',
    venue: '',
    venueAddress: '',
    venueDetails: {
      refreshments: false,
      refreshmentsDetails: '',
      wifi: false,
      wifiNetwork: '',
      wifiPassword: '',
      accessibility: false,
      accessibilityDetails: '',
      additionalInfo: ''
    },
    moderators: [],
    bannedChatUsers: [], 
    chatDisclaimer: '',
    chatEnabled: true, // Default to enabled
    moderatorOnlyChat: false, // Default to all users can chat
    featuredImageUrl: ''
  });

  const [icons, setIcons] = useState<Icon[]>(DEFAULT_ICONS);
  const [iconEdits, setIconEdits] = useState<{[iconId: string]: string}>({});
  const [selectedIconIndex, setSelectedIconIndex] = useState<number | null>(null);
  const [newConferenceName, setNewConferenceName] = useState<string>('');
  const [showNewConferenceForm, setShowNewConferenceForm] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'list' | 'edit'>('list');
  
  // Schedule management state
  const [schedule, setSchedule] = useState<ScheduleEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showEventForm, setShowEventForm] = useState<boolean>(false);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [importStatus, setImportStatus] = useState<{
    loading: boolean;
    progress: number;
    message: string;
  }>({ loading: false, progress: 0, message: '' });

  const [editingEvent, setEditingEvent] = useState<ScheduleEvent>({
    id: '',
    title: '',
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    date: '',
  });
  
  // State for conference duplication
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateConferenceName, setDuplicateConferenceName] = useState('');
  const [conferenceToDuplicate, setConferenceToDuplicate] = useState<string | null>(null);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  
  // State for icon editing mode
  const [iconEditMode, setIconEditMode] = useState<'content' | 'links'>('content');
  
  // State for links management
  const [iconLinks, setIconLinks] = useState<{[iconId: string]: Array<{groupName: string, links: LinkItem[]}>}>({});
  
  // Create refs for click outside detection
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Cloud function references - updated to use generic functions
  const createDocumentFunc = httpsCallable(functions, 'createDocument');
  const updateDocumentFunc = httpsCallable(functions, 'updateDocument');

  const [uploadingLinks, setUploadingLinks] = useState<{[key: string]: boolean}>({});
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [uploadErrors, setUploadErrors] = useState<{[key: string]: string}>({});

  // Helper function to format date values for the form inputs without timezone info
  const formatDateForInput = (dateValue: any): string => {
    if (!dateValue) return '';
    
    // Handle Firestore Timestamp with toDate method
    if (isFirestoreTimestamp(dateValue)) {
      if (typeof dateValue.toDate === 'function') {
        // Live Firestore timestamp - format as YYYY-MM-DD
        const date = dateValue.toDate();
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      } else {
        // Serialized timestamp with seconds/nanoseconds
        const date = new Date(dateValue.seconds * 1000);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      }
    }
    
    // Handle standard JS Date
    if (dateValue instanceof Date) {
      return `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`;
    }

    // Handle string dates
    if (typeof dateValue === 'string') {
      try {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }
      } catch (e) {
        console.error('Error parsing date string:', e);
        return '';
      }
    }
    
    console.log('Unhandled date format:', dateValue);
    return '';
  };

  // Schedule helper functions
const getDatesArray = (startDateStr: string, endDateStr: string): string[] => {
  if (!startDateStr || !endDateStr) return [];
  
  // Extract year, month, day from YYYY-MM-DD strings
  const [startYear, startMonth, startDay] = startDateStr.split('-').map(num => parseInt(num, 10));
  const [endYear, endMonth, endDay] = endDateStr.split('-').map(num => parseInt(num, 10));
  
  if (isNaN(startYear) || isNaN(startMonth) || isNaN(startDay) || 
      isNaN(endYear) || isNaN(endMonth) || isNaN(endDay)) {
    return [];
  }
  
  const dates: string[] = [];
  let currentYear = startYear;
  let currentMonth = startMonth;
  let currentDay = startDay;
  
  // Helper to get days in a month
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
  };
  
  // Helper to format date as YYYY-MM-DD
  const formatDate = (year: number, month: number, day: number): string => {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  
  // Add dates until we reach the end date
  while (currentYear < endYear || 
         (currentYear === endYear && currentMonth < endMonth) || 
         (currentYear === endYear && currentMonth === endMonth && currentDay <= endDay)) {
    
    dates.push(formatDate(currentYear, currentMonth, currentDay));
    
    // Increment day
    currentDay++;
    
    // Check if we need to roll over to next month
    const daysInCurrentMonth = getDaysInMonth(currentYear, currentMonth);
    if (currentDay > daysInCurrentMonth) {
      currentDay = 1;
      currentMonth++;
      
      // Check if we need to roll over to next year
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
  }
    console.log("Get dates array retosponse: ", dates)
    return dates;
  };

  const formatDateTab = (dateStr: string, index: number): string => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid';
    
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' };
    return `Day ${index + 1}: ${date.toLocaleDateString('en-UK', options)}`;
  };

  const formatFullDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      timeZone: 'UTC'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (timeStr: string): string => {
    // Convert 24-hour time to 12-hour time with AM/PM
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getEventsForDate = (date: string): ScheduleEvent[] => {
    return schedule.filter(event => event.date === date);
  };

  // Handle venue selection change
  const handleVenueChange = (venueName: string, venueAddress: string) => {
    setFormData(prev => ({
      ...prev,
      venue: venueName,
      venueAddress: venueAddress
    }));
  };

  // Toggle dropdown menu visibility
  const toggleDropdown = (conferenceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveDropdownId(prevId => prevId === conferenceId ? null : conferenceId);
  };

  // Open duplicate conference modal
  const handleDuplicateClick = (conferenceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConferenceToDuplicate(conferenceId);
    setDuplicateConferenceName('');
    setShowDuplicateModal(true);
  };

  // Helper functions for links management
  const getCurrentIconLinks = (iconId: string) => {
    return iconLinks[iconId] || [];
  };

  const addLinkGroup = (iconId: string) => {
    setIconLinks(prev => ({
      ...prev,
      [iconId]: [
        ...getCurrentIconLinks(iconId),
        { groupName: '', links: [] }
      ]
    }));
  };

  const updateGroupName = (iconId: string, groupIndex: number, groupName: string) => {
    setIconLinks(prev => {
      const currentGroups = getCurrentIconLinks(iconId);
      const updatedGroups = [...currentGroups];
      updatedGroups[groupIndex] = { ...updatedGroups[groupIndex], groupName };
      return {
        ...prev,
        [iconId]: updatedGroups
      };
    });
  };

  const addLinkToGroup = (iconId: string, groupIndex: number) => {
    setIconLinks(prev => {
      const currentGroups = getCurrentIconLinks(iconId);
      const updatedGroups = [...currentGroups];
      updatedGroups[groupIndex] = {
        ...updatedGroups[groupIndex],
        links: [
          ...updatedGroups[groupIndex].links,
          {
            id: `link-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            name: '',
            url: '',
            nonExternalMode: false
          }
        ]
      };
      return {
        ...prev,
        [iconId]: updatedGroups
      };
    });
  };

  const updateLink = (iconId: string, groupIndex: number, linkIndex: number, field: keyof LinkItem, value: string | boolean) => {
    setIconLinks(prev => {
      const currentGroups = getCurrentIconLinks(iconId);
      const updatedGroups = [...currentGroups];
      const updatedLinks = [...updatedGroups[groupIndex].links];
      updatedLinks[linkIndex] = { ...updatedLinks[linkIndex], [field]: value };
      updatedGroups[groupIndex] = { ...updatedGroups[groupIndex], links: updatedLinks };
      return {
        ...prev,
        [iconId]: updatedGroups
      };
    });
  };

  const removeLink = (iconId: string, groupIndex: number, linkIndex: number) => {
    setIconLinks(prev => {
      const currentGroups = getCurrentIconLinks(iconId);
      const updatedGroups = [...currentGroups];
      updatedGroups[groupIndex] = {
        ...updatedGroups[groupIndex],
        links: updatedGroups[groupIndex].links.filter((_, index) => index !== linkIndex)
      };
      return {
        ...prev,
        [iconId]: updatedGroups
      };
    });
  };

  const removeGroup = (iconId: string, groupIndex: number) => {
    setIconLinks(prev => ({
      ...prev,
      [iconId]: getCurrentIconLinks(iconId).filter((_, index) => index !== groupIndex)
    }));
  };

  // Click outside handler for dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdownId(null);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle duplicate conference submission
  const handleDuplicateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!duplicateConferenceName.trim() || !conferenceToDuplicate) {
      onError('Conference name is required.');
      return;
    }
    
    setLoading(true);
    try {
      // Generate a simple ID from the name (lowercase, replace spaces with dash)
      const newConferenceId = duplicateConferenceName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      
      // Check if ID is already in use
      const existingConference = conferences.find(conf => conf.id === newConferenceId);
      if (existingConference) {
        onError(`A conference with this name already exists. Please use a different name.`);
        setLoading(false);
        return;
      }
      
      // Get the source conference data
      const sourceConferenceRef = doc(db, 'conferences', conferenceToDuplicate);
      const sourceConferenceSnapshot = await getDoc(sourceConferenceRef);
      
      if (!sourceConferenceSnapshot.exists()) {
        onError(`Source conference not found.`);
        setLoading(false);
        return;
      }
      
      const sourceData = sourceConferenceSnapshot.data() as ConferenceData;
      
      // Process icons for duplication - copy iconConfig and update externalSite
      const duplicateIcons = (sourceData.data.icons || DEFAULT_ICONS).map(icon => {
        const newIcon = { ...icon };
        
        // If the icon has iconConfig (links), update the externalSite property
        if (icon.iconConfig && icon.iconConfig.type === 'links') {
          newIcon.externalSite = `unison.microbt.co.uk/${newConferenceId}/${icon.id}`;
        }
        
        return newIcon;
      });
      
      // Prepare duplicate data
      const duplicateData = {
        id: newConferenceId,
        enabled: false,
        name: duplicateConferenceName,
        startDate: null,
        endDate: null,
        venue: sourceData.data.venue,
        icons: duplicateIcons,
        schedule: sourceData.data.schedule ? 
          sourceData.data.schedule.map(event => ({
            ...event,
            date: '',
            id: `event-${Date.now()}-${Math.floor(Math.random() * 1000)}`
          })) : [],
        moderators: sourceData.data.moderators || [],
        bannedChatUsers: sourceData.data.bannedChatUsers || [],
        chatDisclaimer: sourceData.data.chatDisclaimer || '',
        chatSettings: sourceData.data.chatSettings || {
          mode: 'all_can_send',
          enabled: true
        },
        featuredImageUrl: sourceData.data.featuredImageUrl || ''
      };

      const result = await createDocumentFunc({
        collectionName: 'conferences',
        docId: newConferenceId,
        docName: duplicateConferenceName,
        data: duplicateData
      });
      
      // @ts-ignore (result data structure)
      if (result.data.success) {
        onSuccess(`Conference "${duplicateConferenceName}" created successfully as a duplicate of "${sourceData.name}".`);
        
        // Refresh conferences list
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          setConferences(metadataData.fields || []);
        }
        
        setDuplicateConferenceName('');
        setShowDuplicateModal(false);
        setConferenceToDuplicate(null);
      } else {
        // @ts-ignore
        onError(result.data.message || 'Failed to duplicate conference.');
      }
    } catch (err: any) {
      console.error('Error duplicating conference:', err);
      onError(err.message || 'Failed to duplicate conference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientStorageConfig && !clientStorageService.isReady()) {
      clientStorageService.initializeClientStorage(clientStorageConfig)
        .catch(error => {
          console.error('Failed to initialize client storage:', error);
        });
    }
  }, [clientStorageConfig]);

  // Helper function to generate unique keys for upload tracking
  const getUploadKey = (iconId: string, groupIndex: number, linkIndex: number): string => {
    return `${iconId}-${groupIndex}-${linkIndex}`;
  };

  // File upload handler
  const handleLinkFileUpload = async (
    iconId: string, 
    groupIndex: number, 
    linkIndex: number, 
    file: File
  ) => {
    const uploadKey = getUploadKey(iconId, groupIndex, linkIndex);
    
    // Clear any previous errors
    setUploadErrors(prev => ({ ...prev, [uploadKey]: '' }));

    // Validate file type
    if (!clientStorageService.validateFileType(file)) {
      setUploadErrors(prev => ({ 
        ...prev, 
        [uploadKey]: 'File type not supported. Please upload an image, document, or media file.' 
      }));
      return;
    }

    // Check if client storage is ready
    if (!clientStorageService.isReady()) {
      setUploadErrors(prev => ({ 
        ...prev, 
        [uploadKey]: 'File storage not available. Please contact support.' 
      }));
      return;
    }

    setUploadingLinks(prev => ({ ...prev, [uploadKey]: true }));
    setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));

    try {
      // Upload the file with progress tracking
      const downloadURL = await clientStorageService.uploadFile(
        file,
        'conference-link-uploads',
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [uploadKey]: progress }));
        }
      );

      // Update the link URL
      updateLink(iconId, groupIndex, linkIndex, 'url', downloadURL);
      
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 100 }));
      
      // Clear upload state after a short delay
      setTimeout(() => {
        setUploadingLinks(prev => ({ ...prev, [uploadKey]: false }));
        setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
      }, 1000);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadErrors(prev => ({ 
        ...prev, 
        [uploadKey]: error instanceof Error ? error.message : 'Upload failed' 
      }));
      setUploadingLinks(prev => ({ ...prev, [uploadKey]: false }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
    }
  };

  // File selection handler
  const handleFileSelect = (iconId: string, groupIndex: number, linkIndex: number) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*,.pdf,.doc,.docx,.txt,.rtf,.mp4,.webm,.ogg,.mp3,.wav,.zip,.rar';
    fileInput.style.display = 'none';
    
    fileInput.addEventListener('change', (e) => {
      const input = e.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        const file = input.files[0];
        handleLinkFileUpload(iconId, groupIndex, linkIndex, file);
      }
      
      document.body.removeChild(fileInput);
    });
    
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  // Thumbnail upload handler
  const handleThumbnailUpload = async (
    iconId: string,
    groupIndex: number,
    linkIndex: number,
    file: File
  ) => {
    const uploadKey = `thumb-${getUploadKey(iconId, groupIndex, linkIndex)}`;

    setUploadErrors(prev => ({ ...prev, [uploadKey]: '' }));

    if (!clientStorageService.validateFileType(file)) {
      setUploadErrors(prev => ({
        ...prev,
        [uploadKey]: 'File type not supported. Please upload an image file.'
      }));
      return;
    }

    if (!clientStorageService.isReady()) {
      setUploadErrors(prev => ({
        ...prev,
        [uploadKey]: 'File storage not available. Please contact support.'
      }));
      return;
    }

    setUploadingLinks(prev => ({ ...prev, [uploadKey]: true }));
    setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));

    try {
      const downloadURL = await clientStorageService.uploadFile(
        file,
        'conference-link-uploads',
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [uploadKey]: progress }));
        }
      );

      updateLink(iconId, groupIndex, linkIndex, 'thumbnailUrl', downloadURL);

      setUploadProgress(prev => ({ ...prev, [uploadKey]: 100 }));

      setTimeout(() => {
        setUploadingLinks(prev => ({ ...prev, [uploadKey]: false }));
        setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
      }, 1000);

    } catch (error) {
      console.error('Thumbnail upload failed:', error);
      setUploadErrors(prev => ({
        ...prev,
        [uploadKey]: error instanceof Error ? error.message : 'Upload failed'
      }));
      setUploadingLinks(prev => ({ ...prev, [uploadKey]: false }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
    }
  };

  // Thumbnail file selector
  const handleThumbnailSelect = (iconId: string, groupIndex: number, linkIndex: number) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';

    fileInput.addEventListener('change', (e) => {
      const input = e.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        handleThumbnailUpload(iconId, groupIndex, linkIndex, input.files[0]);
      }
      document.body.removeChild(fileInput);
    });

    document.body.appendChild(fileInput);
    fileInput.click();
  };

  // Load conferences from metadata
  useEffect(() => {
    const loadConferences = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          setConferences(metadataData.fields || []);
        } else {
          setConferences([]);
        }
      } catch (err) {
        console.error('Error loading conferences:', err);
        onError('Failed to load conferences. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadConferences();
  }, [db, currentUser, setLoading, onError]);

// Load conference data when selected
  useEffect(() => {
    const loadConferenceData = async () => {
      if (!selectedConference) {
        setConferenceData(null);
        setFormData({
          enabled: 'true',
          name: '',
          startDate: '',
          endDate: '',
          venue: '',
          venueAddress: '',
          venueDetails: {
            refreshments: false,
            refreshmentsDetails: '',
            wifi: false,
            wifiNetwork: '',
            wifiPassword: '',
            accessibility: false,
            accessibilityDetails: '',
            additionalInfo: ''
          },
          moderators: [],
          bannedChatUsers: [],
          chatDisclaimer: '',
          chatEnabled: true,
          moderatorOnlyChat: false,
          featuredImageUrl: ''
        });
        setIcons(DEFAULT_ICONS);
        setSelectedIconIndex(null);
        setSchedule([]);
        setSelectedDate('');
        setIconLinks({}); // Reset icon links
        return;
      }
      
      setLoading(true);
      try {
        const conferenceRef = doc(db, 'conferences', selectedConference);
        const conferenceSnapshot = await getDoc(conferenceRef);
        
        if (conferenceSnapshot.exists()) {
          const data = conferenceSnapshot.data() as ConferenceData;
          setConferenceData(data);
          
          setFormData({
            enabled: typeof data.data.enabled === 'boolean' 
              ? data.data.enabled.toString() 
              : (data.data.enabled || 'true'),
            name: data.data.name || data.name || '',
            startDate: formatDateForInput(data.data.startDate),
            endDate: formatDateForInput(data.data.endDate),
            venue: data.data.venue?.name || '',
            venueAddress: data.data.venue?.address || '',
            venueDetails: {
              refreshments: data.data.venue?.facilities?.refreshments?.enabled || false,
              refreshmentsDetails: data.data.venue?.facilities?.refreshments?.details || '',
              wifi: data.data.venue?.facilities?.wifi?.enabled || false,
              wifiNetwork: data.data.venue?.facilities?.wifi?.network || '',
              wifiPassword: data.data.venue?.facilities?.wifi?.password || '',
              accessibility: data.data.venue?.facilities?.accessibility?.enabled || false,
              accessibilityDetails: data.data.venue?.facilities?.accessibility?.details || '',
              additionalInfo: data.data.venue?.additionalInfo || ''
            },
            moderators: data.data.moderators || [],
            bannedChatUsers: data.data.bannedChatUsers || [],
            chatDisclaimer: data.data.chatDisclaimer || '',
            chatEnabled: data.data.chatSettings?.enabled !== undefined ? data.data.chatSettings.enabled : true,
            moderatorOnlyChat: data.data.chatSettings?.mode === 'moderators_only',
            featuredImageUrl: data.data.featuredImageUrl || ''
          });

          setSchedule(data.data.schedule || []);
          
          if (data.data.icons && Array.isArray(data.data.icons)) {
            const existingIconsMap: Record<string, Icon> = {};
            data.data.icons.forEach(icon => {
              existingIconsMap[icon.id] = icon;
            });
            
            const mergedIcons = DEFAULT_ICONS.map(defaultIcon => {
              if (existingIconsMap[defaultIcon.id]) {
                return existingIconsMap[defaultIcon.id];
              }
              return defaultIcon;
            });
            
            setIcons(mergedIcons);
            
            // Extract and load existing links data
            const initialIconLinks: {[iconId: string]: Array<{groupName: string, links: LinkItem[]}>} = {};
            mergedIcons.forEach((icon: Icon) => {
              if (icon.iconConfig && icon.iconConfig.type === 'links') {
                initialIconLinks[icon.id] = icon.iconConfig.groups;
              }
            });
            setIconLinks(initialIconLinks);
          } else {
            setIcons(DEFAULT_ICONS);
            setIconLinks({});
          }
          
          setSelectedIconIndex(null);
          
          if (data.data.startDate) {
            const startDateStr = formatDateForInput(data.data.startDate);
            if (startDateStr) {
              setSelectedDate(startDateStr);
            }
          }
          
          setViewMode('edit');
        } else {
          setConferenceData(null);
          onError(`Conference with ID ${selectedConference} not found.`);
        }
      } catch (err) {
        console.error('Error loading conference data:', err);
        onError('Failed to load conference data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadConferenceData();
  }, [db, selectedConference, setLoading, onError]);

  // Handle input changes for conference form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler for nested form input changes
  const handleNestedInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      venueDetails: {
        ...prev.venueDetails,
        [name]: value
      }
    }));
  };

  // Handler for checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      venueDetails: {
        ...prev.venueDetails,
        [name]: checked
      }
    }));
  };

  // Handler for chat settings checkbox changes
  const handleChatSettingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Handler for moderators tags component
  const handleModeratorsChange = (emails: string[]) => {
    setFormData(prev => ({
      ...prev,
      moderators: emails
    }));
  };
  
  // Handler for banned users tags component
  const handleBannedUsersChange = (emails: string[]) => {
    setFormData(prev => ({
      ...prev,
      bannedChatUsers: emails
    }));
  };

  // Handle icon enabled/disabled toggle
  const handleIconToggle = (index: number) => {
    const updatedIcons = [...icons];
    updatedIcons[index].enabled = !updatedIcons[index].enabled;
    setIcons(updatedIcons);
  };

    // Handle icon selection for editing
    const handleIconSelect = (index: number) => {
        // Prevent selecting the "Follow Us" icon
        if (icons[index].id === 'follow-us') {
            return;
        }

        if (selectedIconIndex !== null) {
            const currentIconId = icons[selectedIconIndex].id;
            const hasUnsavedEdits = iconEditsRef.current[currentIconId] !== undefined;
            if (hasUnsavedEdits) {
            console.log(`Icon ${currentIconId} has unsaved edits`);
            }
        }

        console.log(`Selecting icon at index ${index}:`, icons[index]);
        setSelectedIconIndex(index);

        // Set the appropriate edit mode based on whether the icon has links
        const selectedIcon = icons[index];
        const hasLinks = iconLinks[selectedIcon.id] && iconLinks[selectedIcon.id].length > 0;

        // Default to 'content' mode if no links, 'links' mode if links exist
        setIconEditMode(hasLinks ? 'links' : 'content');
    };

  const iconEditsRef = useRef<{[iconId: string]: string}>({});

  // Handle icon HTML update
  const handleIconHtmlUpdate = (html: string) => {
    if (selectedIconIndex !== null) {
      const iconId = icons[selectedIconIndex].id;
      iconEditsRef.current[iconId] = html;
      console.log(`Storing edit for icon ${iconId}`);
      console.log(`New HTML:`, html);
    }
  };

  const getCurrentIconHtml = (iconIndex: number): string => {
    const icon = icons[iconIndex];
    if (!icon) return '';
    return iconEditsRef.current[icon.id] ?? icon.HTML ?? '';
  };
  
  // Handle icon name update
  const handleIconNameUpdate = (name: string) => {
    if (selectedIconIndex !== null) {
      const updatedIcons = [...icons];
      updatedIcons[selectedIconIndex].name = name;
      setIcons(updatedIcons);
    }
  };

  // Schedule event handlers
  const handleEventInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditingEvent(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Event submit function
  const handleEventSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!editingEvent.title || !editingEvent.startTime || !editingEvent.endTime || !editingEvent.location || !editingEvent.date) {
      onError('Please fill in all required fields: title, time, location, and date.');
      return;
    }
    
    try {
      const updatedSchedule = [...schedule];
      
      if (editingEvent.id) {
        const eventIndex = updatedSchedule.findIndex(event => event.id === editingEvent.id);
        if (eventIndex !== -1) {
          updatedSchedule[eventIndex] = {...editingEvent};
        }
      } else {
        updatedSchedule.push({
          ...editingEvent,
          id: `event-${Date.now()}`
        });
      }
      
      setSchedule(updatedSchedule);
      setShowEventForm(false);
      
      setTimeout(() => {
        setEditingEvent({
          id: '',
          title: '',
          startTime: '09:00',
          endTime: '10:00',
          location: '',
          date: selectedDate,
        });
      }, 100);
      
      onSuccess(`Event "${editingEvent.title}" ${editingEvent.id ? 'updated' : 'added'} successfully.`);
    } catch (err) {
      console.error('Error updating schedule:', err);
      onError('Failed to update the schedule. Please try again.');
    }
  };

  const handleDeleteEvent = (eventId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        const updatedSchedule = schedule.filter(event => event.id !== eventId);
        setSchedule(updatedSchedule);
        onSuccess('Event deleted successfully.');
      } catch (err) {
        console.error('Error deleting event:', err);
        onError('Failed to delete the event. Please try again.');
      }
    }
  };

  // Handle imported events
  const handleScheduleImport = (importedEvents: ScheduleEvent[], importOption: ImportOption) => {
    setLoading(true);
    setImportStatus({ loading: true, progress: 10, message: 'Processing imported events...' });
    
    try {
      let updatedSchedule = [...schedule];
      
      setTimeout(() => {
        try {
          setImportStatus({ loading: true, progress: 30, message: 'Applying import options...' });
          
          switch (importOption) {
            case 'replace':
              updatedSchedule = importedEvents;
              break;
              
            case 'replace-dates':
              const importDatesMap: { [key: string]: boolean } = {};
              importedEvents.forEach(event => {
                importDatesMap[event.date] = true;
              });
              const importDates = Object.keys(importDatesMap);
              
              updatedSchedule = schedule.filter(event => !importDates.includes(event.date));
              updatedSchedule = updatedSchedule.concat(importedEvents);
              break;
              
            case 'add':
            default:
              updatedSchedule = updatedSchedule.concat(importedEvents);
              break;
          }
          
          setImportStatus({ loading: true, progress: 70, message: 'Updating schedule...' });
          setSchedule(updatedSchedule);
          
          if (importedEvents.length > 0) {
            const datesMap: { [key: string]: boolean } = {};
            importedEvents.forEach(event => {
              datesMap[event.date] = true;
            });
            const sortedDates = Object.keys(datesMap).sort();
            
            if (sortedDates.length > 0) {
              setSelectedDate(sortedDates[0]);
            }
          }
          
          setImportStatus({ loading: true, progress: 100, message: 'Import complete!' });
          
          setTimeout(() => {
            setLoading(false);
            setImportStatus({ loading: false, progress: 0, message: '' });
            onSuccess(`Successfully imported ${importedEvents.length} events.`);
          }, 500);
          
        } catch (err) {
          console.error('Error processing imported events:', err);
          setLoading(false);
          setImportStatus({ loading: false, progress: 0, message: '' });
          onError('An error occurred while processing the imported events.');
        }
      }, 100);
      
    } catch (err) {
      console.error('Error starting import process:', err);
      setLoading(false);
      setImportStatus({ loading: false, progress: 0, message: '' });
      onError('Failed to process the import. Please try again.');
    }
  };

  const handleFileUpload = async (file: File) => {
    setLoading(true);
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = parseExcelSchedule(arrayBuffer);
      
      if (result.errors.length > 0) {
        onError(result.errors[0]);
        setLoading(false);
        return;
      }
      
      handleScheduleImport(result.events, 'add');
      
      if (result.warnings.length > 0) {
        const warningMsg = result.warnings.join(' ');
        onSuccess(`Imported ${result.events.length} events. Note: ${warningMsg}`);
      } else {
        onSuccess(`Successfully imported ${result.events.length} events from the Excel file.`);
      }
      
    } catch (err) {
      console.error('Error processing Excel file:', err);
      onError('Failed to process the Excel file. Please check the format and try again.');
      setLoading(false);
    }
  };

  const handleImportButtonClick = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.xlsx,.xls';
    fileInput.style.display = 'none';
    
    fileInput.addEventListener('change', (e) => {
      const input = e.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        const file = input.files[0];
        if (file.name.match(/\.(xlsx|xls)$/)) {
          handleFileUpload(file);
        } else {
          onError('Please upload an Excel file (.xlsx or .xls)');
        }
      }
      
      document.body.removeChild(fileInput);
    });
    
    document.body.appendChild(fileInput);
    fileInput.click();
  };

    // Update conference using the generic updateDocument cloud function
    const handleUpdateConference = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedConference || !conferenceData) return;

        setLoading(true);
        try {
            let startDate = null;
            let endDate = null;

            if (formData.startDate) {
            const [year, month, day] = formData.startDate.split('-').map(num => parseInt(num, 10));
            startDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            }

            if (formData.endDate) {
            const [year, month, day] = formData.endDate.split('-').map(num => parseInt(num, 10));
            endDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            }

            const venueData = {
            name: formData.venue,
            address: formData.venueAddress,
            additionalInfo: formData.venueDetails.additionalInfo,
            facilities: {
                refreshments: {
                enabled: formData.venueDetails.refreshments,
                details: formData.venueDetails.refreshmentsDetails
                },
                wifi: {
                enabled: formData.venueDetails.wifi,
                network: formData.venueDetails.wifiNetwork,
                password: formData.venueDetails.wifiPassword
                },
                accessibility: {
                enabled: formData.venueDetails.accessibility,
                details: formData.venueDetails.accessibilityDetails
                }
            }
            };

            // Apply any pending icon edits and add iconConfig for icons with links
            const iconsWithEditsAndConfig = icons.map(icon => {
            // ALWAYS enforce the "Follow Us" icon structure - never allow edits
            if (icon.id === 'follow-us') {
                return {
                id: "follow-us",
                name: "Follow Us",
                enabled: true,
                HTML: '<a href="https://www.jotlu.com/links/unison-conferences/">Follow Us</a>',
                externalSite: 'https://www.jotlu.com/links/unison-conferences/'
                };
            }

            const iconWithEdits = {
                ...icon,
                HTML: iconEditsRef.current[icon.id] ?? icon.HTML
            };

            // Add iconConfig and externalSite if this icon has links
            const iconLinksData = iconLinks[icon.id];
            if (iconLinksData && iconLinksData.length > 0) {
                iconWithEdits.iconConfig = {
                type: 'links' as const,
                groups: iconLinksData
                };
                iconWithEdits.externalSite = `unison.microbt.co.uk/${selectedConference}/${icon.id}`;
                iconWithEdits.HTML = `${icon.name}`
            } else {
                // Remove iconConfig and externalSite if no links
                delete iconWithEdits.iconConfig;
                delete iconWithEdits.externalSite;
            }

            return iconWithEdits;
            });

            // Clean HTML content in all icons before saving (except Follow Us which is already set)
            const cleanedIcons = iconsWithEditsAndConfig.map(icon => {
            // Skip cleaning for Follow Us - it's already in the correct format
            if (icon.id === 'follow-us') {
                return icon;
            }
            
            try {
                return {
                ...icon,
                HTML: cleanHtml(icon.HTML)
                };
            } catch (err) {
                console.warn(`Error cleaning HTML for icon ${icon.id}:`, err);
                return icon;
            }
            });

            const conferenceData = {
            enabled: formData.enabled === 'true',
            name: formData.name,
            startDate: startDate,
            endDate: endDate,
            venue: venueData,
            icons: cleanedIcons,
            schedule: schedule,
            moderators: formData.moderators,
            bannedChatUsers: formData.bannedChatUsers,
            chatDisclaimer: formData.chatDisclaimer,
            chatSettings: {
                mode: formData.moderatorOnlyChat ? 'moderators_only' : 'all_can_send',
                enabled: formData.chatEnabled
            },
            featuredImageUrl: formData.featuredImageUrl
            };

            const result = await updateDocumentFunc({
            collectionName: 'conferences',
            docId: selectedConference,
            data: conferenceData
            });

            // @ts-ignore (result data structure)
            if (result.data.success) {
            iconEditsRef.current = {};
            setIcons(cleanedIcons);
            onSuccess(`Conference "${formData.name}" updated successfully.`);

            const conferenceRef = doc(db, 'conferences', selectedConference);
            const conferenceSnapshot = await getDoc(conferenceRef);
            if (conferenceSnapshot.exists()) {
                setConferenceData(conferenceSnapshot.data() as ConferenceData);
            }

            const metadataRef = doc(db, 'metadata', 'conferences');
            const metadataSnapshot = await getDoc(metadataRef);
            if (metadataSnapshot.exists()) {
                const metadataData = metadataSnapshot.data() as MetadataCollection;
                setConferences(metadataData.fields || []);
            }

            setViewMode('list');
            setSelectedConference(null);
            } else {
            // @ts-ignore
            onError(result.data.message || 'Failed to update conference.');
            }
        } catch (err: any) {
            console.error('Error updating conference:', err);
            onError(err.message || 'Failed to update conference. Please try again.');
        } finally {
            setLoading(false);
        }
    };

  // Create new conference
  const handleCreateConference = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newConferenceName.trim()) {
      onError('Conference name is required.');
      return;
    }

    setLoading(true);
    try {
      const conferenceId = newConferenceName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      
      const existingConference = conferences.find(conf => conf.id === conferenceId);
      if (existingConference) {
        onError(`A conference with this name already exists. Please use a different name.`);
        setLoading(false);
        return;
      }
      
      const initialIcons = DEFAULT_ICONS.map(icon => ({
        ...icon,
        enabled: true
      }));
      
      const initialVenueData = {
        name: "",
        address: "",
        additionalInfo: "",
        facilities: {
          refreshments: { enabled: false, details: "" },
          wifi: { enabled: false, network: "", password: "" },
          accessibility: { enabled: false, details: "" }
        }
      };
      
      const result = await createDocumentFunc({
        collectionName: 'conferences',
        docId: conferenceId,
        docName: newConferenceName,
        data: {
          id: conferenceId,
          enabled: true,
          name: newConferenceName,
          startDate: null,
          endDate: null,
          venue: initialVenueData,
          icons: initialIcons,
          schedule: [],
          moderators: [],
          bannedChatUsers: [],
          chatDisclaimer: "",
          chatSettings: {
            mode: 'all_can_send',
            enabled: true
          },
          featuredImageUrl: ""
        }
      });

      // @ts-ignore (result data structure)
      if (result.data.success) {
        onSuccess(`Conference "${newConferenceName}" created successfully.`);
        
        const metadataRef = doc(db, 'metadata', 'conferences');
        const metadataSnapshot = await getDoc(metadataRef);
        
        if (metadataSnapshot.exists()) {
          const metadataData = metadataSnapshot.data() as MetadataCollection;
          setConferences(metadataData.fields || []);
        }
        
        setNewConferenceName('');
        setShowNewConferenceForm(false);
      } else {
        // @ts-ignore
        onError(result.data.message || 'Failed to create conference.');
      }
    } catch (err: any) {
      console.error('Error creating conference:', err);
      onError(err.message || 'Failed to create conference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle back to list view
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedConference(null);
  };

  // Render the Duplicate Conference Modal
  const renderDuplicateModal = () => {
    if (!showDuplicateModal) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Duplicate Conference</h3>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowDuplicateModal(false)}
            >
              <span className="icon-close">✖️</span>
            </button>
          </div>

          <form onSubmit={handleDuplicateSubmit} className="duplicate-form">
            <div className="form-field">
              <label>New Conference Name</label>
              <input
                type="text"
                value={duplicateConferenceName}
                onChange={(e) => setDuplicateConferenceName(e.target.value)}
                placeholder="Enter name for the duplicated conference"
                required
              />
              <small className="helper-text">This will create a copy of the conference.</small>
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={!duplicateConferenceName.trim()}
              >
                Create Duplicate
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => setShowDuplicateModal(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Event form modal
  const renderEventFormModal = () => {
    if (!showEventForm) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>{editingEvent.id ? 'Edit Event' : 'Add New Event'}</h3>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowEventForm(false)}
            >
              <span className="icon-close">✖️</span>
            </button>
          </div>

          <div className="event-form">
            <div className="form-field">
              <label>Event Title</label>
              <input 
                type="text"
                name="title"
                value={editingEvent.title}
                onChange={handleEventInputChange}
                placeholder="Enter event title"
              />
            </div>
            
            <div className="form-row">
              <div className="form-field">
                <label>Start Time</label>
                <input 
                  type="time"
                  name="startTime"
                  value={editingEvent.startTime}
                  onChange={handleEventInputChange}
                />
              </div>
              <div className="form-field">
                <label>End Time</label>
                <input 
                  type="time"
                  name="endTime"
                  value={editingEvent.endTime}
                  onChange={handleEventInputChange}
                />
              </div>
            </div>
            
            <div className="form-field">
              <label>Location</label>
              <input 
                type="text"
                name="location"
                value={editingEvent.location}
                onChange={handleEventInputChange}
                placeholder="Enter event location"
              />
            </div>
            
            <div className="form-field">
              <label>Description (Optional)</label>
              <textarea
                name="description"
                value={editingEvent.description || ''}
                onChange={handleEventInputChange}
                placeholder="Enter event description"
                rows={3}
              />
            </div>

            <div className="form-field">
              <label>Event Type (for color coding)</label>
              <select
                name="type"
                value={editingEvent.type || ''}
                onChange={handleEventInputChange}
              >
                <option value="">Default</option>
                <option value="break">Break</option>
                <option value="workshop">Workshop</option>
                <option value="ceremony">Ceremony</option>
                <option value="panel">Panel</option>
              </select>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="save-btn"
                onClick={handleEventSubmit}
              >
                {editingEvent.id ? 'Update Event' : 'Add Event'}
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => setShowEventForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderExcelImportModal = () => (
    <ExcelImportModal
      isOpen={showImportModal}
      onClose={() => setShowImportModal(false)}
      onImport={handleScheduleImport}
      onError={onError}
      onSuccess={onSuccess}
      currentScheduleSize={schedule.length}
    />
  );

  // Render the conference list view
  const renderConferenceList = () => (
    <div className="conference-list-view">
      <div className="view-header">
        <h2>Conference Management</h2>
        <button
          type="button"
          className="add-conference-btn"
          onClick={() => setShowNewConferenceForm(!showNewConferenceForm)}
        >
          {showNewConferenceForm ? 'Cancel' : '+ Add Conference'}
        </button>
      </div>

      {showNewConferenceForm && (
        <div className="new-conference-form">
          <h3>Create New Conference</h3>
          <form onSubmit={handleCreateConference}>
            <div className="form-field">
              <label>Conference Name</label>
              <input
                type="text"
                value={newConferenceName}
                onChange={(e) => setNewConferenceName(e.target.value)}
                placeholder="Enter conference name"
                required
              />
            </div>
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={!newConferenceName.trim()}
              >
                Create Conference
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => setShowNewConferenceForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="conference-cards">
        {conferences.length > 0 ? (
          conferences.map(conference => (
            <div
              key={conference.id}
              className="conference-card"
            >
              <div className="card-content">
                <h3 className="card-title">{conference.name}</h3>
                <div className="card-status">
                  <span className={`status-indicator ${conference.enabled ? 'enabled' : 'disabled'}`}>
                    {conference.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="card-footer">
                  <div className="card-actions">
                    <button 
                      type="button"
                      className="duplicate-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateClick(conference.id, e);
                      }}
                    >
                      Duplicate
                    </button>
                    <button 
                      type="button"
                      className="edit-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedConference(conference.id);
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>No conferences available. Create your first conference to get started.</p>
          </div>
        )}
      </div>
      
      {renderDuplicateModal()}
    </div>
  );

  // Render icon content editor
  const renderIconContentEditor = () => {
    if (selectedIconIndex === null) return null;
    
    const iconIndex = selectedIconIndex;
    
    return (
      <div className="icon-content-field">
        <label>Icon Content</label>
        <WysiwygEditor
          key={`icon-editor-${iconIndex}`}
          initialHtml={getCurrentIconHtml(iconIndex)}
          onChange={handleIconHtmlUpdate}
          clientStorageConfig={clientStorageConfig}
        />
      </div>
    );
  };

  // Updated renderLinksPageEditor function
  const renderLinksPageEditor = () => {
    if (selectedIconIndex === null) return null;

    const currentIcon = icons[selectedIconIndex];
    const canUpload = clientStorageConfig && clientStorageService.isReady();

    return (
      <div className="icon-links-field">
        <div className="links-header">
          <label>Links Page</label>
          <button
            type="button"
            className="add-group-btn"
            onClick={() => addLinkGroup(currentIcon.id)}
          >
            + Add Group
          </button>
        </div>

        <div className="links-groups">
          {getCurrentIconLinks(currentIcon.id).map((group, groupIndex) => (
            <div key={groupIndex} className="link-group">
              <div className="group-header">
                <input
                  type="text"
                  value={group.groupName}
                  onChange={(e) => updateGroupName(currentIcon.id, groupIndex, e.target.value)}
                  placeholder="Group Name"
                  className="group-name-input"
                />
                <button
                  type="button"
                  className="remove-group-btn"
                  onClick={() => removeGroup(currentIcon.id, groupIndex)}
                  title="Remove Group"
                >
                  ✕
                </button>
              </div>

              <div className="group-links">
                {group.links.map((link, linkIndex) => {
                  const uploadKey = getUploadKey(currentIcon.id, groupIndex, linkIndex);
                  const thumbUploadKey = `thumb-${uploadKey}`;
                  const isUploading = uploadingLinks[uploadKey];
                  const isThumbUploading = uploadingLinks[thumbUploadKey];
                  const progress = uploadProgress[uploadKey] || 0;
                  const thumbProgress = uploadProgress[thumbUploadKey] || 0;
                  const error = uploadErrors[uploadKey];
                  const thumbError = uploadErrors[thumbUploadKey];

                  return (
                    <div key={linkIndex} className="link-item">

                      {/* Link Name */}
                      <input
                        type="text"
                        value={link.name}
                        onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'name', e.target.value)}
                        placeholder="Link Name"
                        className="link-name-input"
                        disabled={isUploading || isThumbUploading}
                      />

                      {/* Thumbnail upload */}
                      <div className="link-thumbnail-row">
                        <span className="link-field-label">Thumbnail</span>
                        <div className="link-thumbnail-preview">
                          {link.thumbnailUrl ? (
                            <img src={link.thumbnailUrl} alt="thumbnail" className="link-thumbnail-img" />
                          ) : (
                            <div className="link-thumbnail-empty">No image</div>
                          )}
                        </div>
                        <input
                          type="text"
                          value={link.thumbnailUrl || ''}
                          onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'thumbnailUrl', e.target.value)}
                          placeholder="Image URL or upload..."
                          className="link-thumbnail-url-input"
                          disabled={isThumbUploading}
                        />
                        {canUpload && (
                          <button
                            type="button"
                            className="file-upload-btn"
                            onClick={() => handleThumbnailSelect(currentIcon.id, groupIndex, linkIndex)}
                            disabled={isThumbUploading}
                            title="Upload thumbnail image"
                          >
                            {isThumbUploading ? '⏳' : '📁'}
                          </button>
                        )}
                        {isThumbUploading && (
                          <div className="upload-progress">
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${thumbProgress}%` }} />
                            </div>
                            <span className="progress-text">Uploading... {Math.round(thumbProgress)}%</span>
                          </div>
                        )}
                        {thumbError && (
                          <div className="upload-error">
                            <span className="error-icon">⚠️</span>
                            {thumbError}
                          </div>
                        )}
                      </div>

                      {/* nonExternalMode toggle */}
                      <div className="link-mode-toggle">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={!!link.nonExternalMode}
                            onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'nonExternalMode', e.target.checked)}
                            disabled={isUploading || isThumbUploading}
                          />
                          <span>Detail view (no external URL)</span>
                        </label>
                      </div>

                      {/* URL field — shown when nonExternalMode is false/null */}
                      {!link.nonExternalMode && (
                        <div className="link-url-container">
                          <input
                            type="url"
                            value={link.url || ''}
                            onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'url', e.target.value)}
                            placeholder="https://example.com"
                            className="link-url-input"
                            disabled={isUploading}
                          />
                          {canUpload && (
                            <button
                              type="button"
                              className="file-upload-btn"
                              onClick={() => handleFileSelect(currentIcon.id, groupIndex, linkIndex)}
                              disabled={isUploading}
                              title="Upload file"
                            >
                              {isUploading ? '⏳' : '📁'}
                            </button>
                          )}
                        </div>
                      )}

                      {/* URL upload progress */}
                      {isUploading && (
                        <div className="upload-progress">
                          <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${progress}%` }} />
                          </div>
                          <span className="progress-text">Uploading... {Math.round(progress)}%</span>
                        </div>
                      )}

                      {error && (
                        <div className="upload-error">
                          <span className="error-icon">⚠️</span>
                          {error}
                        </div>
                      )}

                      {/* Detail view fields — shown when nonExternalMode is true */}
                      {link.nonExternalMode && (
                        <div className="link-detail-fields">
                          <textarea
                            value={link.description || ''}
                            onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'description', e.target.value)}
                            placeholder="About / description"
                            className="link-detail-textarea"
                            rows={4}
                          />
                          <input
                            type="text"
                            value={link.availability || ''}
                            onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'availability', e.target.value)}
                            placeholder="Availability (e.g. Monday and Tuesday)"
                            className="link-detail-input"
                          />
                          <input
                            type="text"
                            value={link.promotions || ''}
                            onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'promotions', e.target.value)}
                            placeholder="Promotions (optional)"
                            className="link-detail-input"
                          />
                          <input
                            type="text"
                            value={link.otherInfo || ''}
                            onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'otherInfo', e.target.value)}
                            placeholder="Other info / website URL (optional)"
                            className="link-detail-input"
                          />
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={!!link.appointmentsRequired}
                              onChange={(e) => updateLink(currentIcon.id, groupIndex, linkIndex, 'appointmentsRequired', e.target.checked)}
                            />
                            <span>Appointments required</span>
                          </label>
                        </div>
                      )}

                      {/* Remove link */}
                      <button
                        type="button"
                        className="remove-link-btn"
                        onClick={() => removeLink(currentIcon.id, groupIndex, linkIndex)}
                        title="Remove Link"
                        disabled={isUploading || isThumbUploading}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}

                <button
                  type="button"
                  className="add-link-btn"
                  onClick={() => addLinkToGroup(currentIcon.id, groupIndex)}
                >
                  + Add Link
                </button>
              </div>
            </div>
          ))}

          {getCurrentIconLinks(currentIcon.id).length === 0 && (
            <div className="empty-links-message">
              <p>No link groups created yet. Click "Add Group" to create your first group.</p>
            </div>
          )}
        </div>

        {/* File upload info */}
        {canUpload && (
          <div className="upload-info">
            <small>
              📁 Click the folder icon to upload a file or image (max 10MB).
              <br />
              Supported: Images, PDFs, Documents, Media files
            </small>
          </div>
        )}

        {!canUpload && (
          <div className="upload-disabled-info">
            <small>
              ℹ️ File upload not available. Enter URLs manually.
            </small>
          </div>
        )}
      </div>
    );
  };

  // The final return statement with the complete UI
  return (
    <div className="conference-manager">
      {viewMode === 'list' ? renderConferenceList() : (
        <div className="conference-edit-view">
          <div className="view-header">
            <button 
              type="button"
              className="back-btn" 
              onClick={handleBackToList}
            >
              ← Back to Conferences
            </button>
            <h2>Edit Conference: {conferenceData?.name}</h2>
          </div>
          
          <form onSubmit={handleUpdateConference} className="edit-conference-form">
            <div className="form-section">
              <h3>Conference Details</h3>
              
              <div className="form-field">
                <label>Status</label>
                <select
                  name="enabled"
                  value={formData.enabled}
                  onChange={handleInputChange}
                  className="status-select"
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
              
              <div className="form-field">
                <label>Conference Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter conference name"
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-field">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-field">
                <label>Home Screen Image URL</label>
                <input
                  type="text"
                  name="featuredImageUrl"
                  value={formData.featuredImageUrl}
                  onChange={handleInputChange}
                  placeholder="Enter URL for home screen featured image"
                />
                <small className="helper-text">This image will be displayed on the conference home screen</small>
              </div>
            </div>
            
            {/* Chat Settings Section */}
            <div className="form-section">
              <h3>Chat Settings</h3>
              <p className="section-description">
                Configure chat moderation and disclaimer settings
              </p>
              
              <div className="form-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="chatEnabled"
                    checked={formData.chatEnabled}
                    onChange={handleChatSettingChange}
                  />
                  <span>Enable Chat</span>
                </label>
                <small className="helper-text">When disabled, users won't be able to send messages in the chat.</small>
              </div>
              
              <div className="form-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="moderatorOnlyChat"
                    checked={formData.moderatorOnlyChat}
                    onChange={handleChatSettingChange}
                  />
                  <span>Moderator-Only Chat</span>
                </label>
                <small className="helper-text">When enabled, only moderators can send messages in the chat.</small>
              </div>
              
              <div className="form-field">
                <label>Chat Moderators</label>
                <EmailTagsInput 
                  emails={formData.moderators}
                  onChange={handleModeratorsChange}
                  placeholder="Add moderator email..."
                />
                <small className="helper-text">Add email addresses for chat moderators. Press Enter, comma, or space after each email.</small>
              </div>
              
              <div className="form-field">
                <label>Chat Banned Users</label>
                <EmailTagsInput 
                  emails={formData.bannedChatUsers}
                  onChange={handleBannedUsersChange}
                  placeholder="Add banned user email..."
                />
                <small className="helper-text">Add email addresses for users banned from chat. Press Enter, comma, or space after each email.</small>
              </div>
              
              <div className="form-field">
                <label>Chat Disclaimer</label>
                <textarea
                  name="chatDisclaimer"
                  value={formData.chatDisclaimer}
                  onChange={handleInputChange}
                  placeholder="Enter chat disclaimer text"
                  rows={4}
                  className="form-textarea"
                />
                <small className="helper-text">This text will be displayed to users before they can access the chat</small>
              </div>
            </div>
            
            {/* Venue Details Section */}
            <div className="form-section">
              <h3>Venue Details</h3>
              <p className="section-description">
                Provide information about the conference venue
              </p>
              
              <VenueSelector
                selectedVenue={formData.venue}
                selectedAddress={formData.venueAddress}
                onVenueChange={handleVenueChange}
              />
              
              <div className="form-field">
                <label>Additional Venue Information</label>
                <textarea
                  name="additionalInfo"
                  value={formData.venueDetails.additionalInfo}
                  onChange={handleNestedInputChange}
                  placeholder="Additional information about the venue (optional)"
                  rows={3}
                  className="form-textarea"
                />
              </div>
              
              <div className="venue-facilities">
                <h4>Venue Facilities</h4>
                
                <div className="facility-item">
                  <div className="facility-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="refreshments"
                        checked={formData.venueDetails.refreshments}
                        onChange={handleCheckboxChange}
                      />
                      <span>Refreshments</span>
                    </label>
                  </div>
                  
                  {formData.venueDetails.refreshments && (
                    <div className="facility-details">
                      <input
                        type="text"
                        name="refreshmentsDetails"
                        value={formData.venueDetails.refreshmentsDetails}
                        onChange={handleNestedInputChange}
                        placeholder="Refreshments details (e.g. 'Available in the Main Lobby & Floor 2')"
                      />
                    </div>
                  )}
                </div>
                
                <div className="facility-item">
                  <div className="facility-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="wifi"
                        checked={formData.venueDetails.wifi}
                        onChange={handleCheckboxChange}
                      />
                      <span>Wi-Fi</span>
                    </label>
                  </div>
                  
                  {formData.venueDetails.wifi && (
                    <div className="facility-details">
                      <div className="form-row">
                        <div className="form-field">
                          <label>Network Name</label>
                          <input
                            type="text"
                            name="wifiNetwork"
                            value={formData.venueDetails.wifiNetwork}
                            onChange={handleNestedInputChange}
                            placeholder="Wi-Fi network name"
                          />
                        </div>
                        <div className="form-field">
                          <label>Password</label>
                          <input
                            type="text"
                            name="wifiPassword"
                            value={formData.venueDetails.wifiPassword}
                            onChange={handleNestedInputChange}
                            placeholder="Wi-Fi password"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="facility-item">
                  <div className="facility-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="accessibility"
                        checked={formData.venueDetails.accessibility}
                        onChange={handleCheckboxChange}
                      />
                      <span>Accessibility</span>
                    </label>
                  </div>
                  
                  {formData.venueDetails.accessibility && (
                    <div className="facility-details">
                      <input
                        type="text"
                        name="accessibilityDetails"
                        value={formData.venueDetails.accessibilityDetails}
                        onChange={handleNestedInputChange}
                        placeholder="Accessibility details (e.g. 'All areas are wheelchair accessible')"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Schedule Management Section */}
            <div className="form-section">
              <h3>Conference Schedule</h3>
              <p className="section-description">
                Manage the conference schedule by adding events to each day
              </p>

              <div className="schedule-management">
                <div className="schedule-dates">
                  {getDatesArray(formData.startDate, formData.endDate).map((date, index) => (
                    <button
                      type="button"
                      key={date}
                      className={`date-tab ${selectedDate === date ? 'active' : ''}`}
                      onClick={() => setSelectedDate(date)}
                    >
                      {formatDateTab(date, index)}
                    </button>
                  ))}
                  
                  {(!formData.startDate || !formData.endDate) && (
                    <div className="empty-schedule-message">
                      <span className="icon-calendar">📅</span>
                      <p>Set conference start and end dates to manage the schedule</p>
                    </div>
                  )}
                </div>

                {selectedDate && (
                  <div className="schedule-events-container">
                    <div className="schedule-header">
                      <h4>{formatFullDate(selectedDate)}</h4>
                      <div className="schedule-actions">
                        <button
                          type="button"
                          className="import-excel-btn"
                          onClick={() => setShowImportModal(true)}
                          title="Import schedule from Excel file"
                        >
                          <span className="icon-excel">📊</span>
                          Import Excel
                        </button>
                        <button
                          type="button"
                          className="add-event-btn"
                          onClick={() => {
                            setEditingEvent({
                              id: '',
                              title: '',
                              startTime: '09:00',
                              endTime: '10:00',
                              location: '',
                              date: selectedDate,
                            });
                            setShowEventForm(true);
                          }}
                        >
                          <span className="icon-add">➕</span>
                          Add Event
                        </button>
                      </div>
                    </div>

                    <div className="schedule-events-list">
                      {getEventsForDate(selectedDate).length > 0 ? (
                        getEventsForDate(selectedDate)
                          .sort((a, b) => a.startTime.localeCompare(b.startTime))
                          .map((event) => (
                            <div 
                              key={event.id} 
                              className={`schedule-event-item ${event.type || ''}`}
                              onClick={() => {
                                setEditingEvent(event);
                                setShowEventForm(true);
                              }}
                            >
                              <div className="event-time">
                                {formatTime(event.startTime)} - {formatTime(event.endTime)}
                              </div>
                              <div className="event-details">
                                <h5>{event.title}</h5>
                                <p>{event.location}</p>
                              </div>
                              <button
                                type="button"
                                className="delete-event-btn"
                                onClick={(e) => handleDeleteEvent(event.id, e)}
                              >
                                <span className="icon-delete" style={{ color: '#ff6b6b' }}>🗑️</span>
                              </button>
                            </div>
                          ))
                      ) : (
                        <div className="empty-events-message">
                          <p>No events scheduled for this day. Click "Add Event" to create one.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Icons Section */}
            <div className="form-section">
              <h3>Conference Icons</h3>
              <p className="section-description">
                Select which icons to display and edit their content
              </p>
              
              <div className="icons-editor">
                <div className="icons-list-panel">
                <div className="icons-list">
                    {icons.map((icon, index) => (
                        <div
                        key={icon.id}
                        className={`icon-item ${selectedIconIndex === index ? 'selected' : ''} ${icon.id === 'follow-us' ? 'disabled' : ''}`}
                        onClick={() => handleIconSelect(index)}
                        style={{
                            opacity: icon.id === 'follow-us' ? 0.6 : 1,
                            cursor: icon.id === 'follow-us' ? 'not-allowed' : 'pointer'
                        }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', cursor: icon.id === 'follow-us' ? 'not-allowed' : 'pointer' }}>
                                <input
                                type="checkbox"
                                checked={icon.enabled}
                                onChange={() => icon.id === 'follow-us' ? null : handleIconToggle(index)}
                                disabled={icon.id === 'follow-us'}
                                style={{ flexShrink: 0, margin: 0, cursor: icon.id === 'follow-us' ? 'not-allowed' : 'pointer' }}
                                />
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {icon.name}
                                {icon.id === 'follow-us' && <span style={{ fontSize: '0.8em', marginLeft: '4px', color: '#666' }}>(locked)</span>}
                                </span>
                            </div>
                        </div>
                    ))}
                    </div>
                </div>
                
                <div className="icon-edit-panel">
                  {selectedIconIndex !== null ? (
                    <div className="icon-editor">
                      <div className="editor-header">
                        <h4>Editing: {icons[selectedIconIndex].name}</h4>
                        <div className="icon-name-field">
                          <label>Icon Name</label>
                          <input
                            type="text"
                            value={icons[selectedIconIndex].name}
                            onChange={(e) => handleIconNameUpdate(e.target.value)}
                            placeholder="Enter icon name"
                          />
                        </div>
                      </div>
                      
                      {/* Mode Toggle */}
                      <div className="icon-mode-toggle">
                        <div className="toggle-buttons">
                          <button
                            type="button"
                            className={`toggle-btn ${iconEditMode === 'content' ? 'active' : ''}`}
                            onClick={() => setIconEditMode('content')}
                          >
                            Icon Content
                          </button>
                          <button
                            type="button"
                            className={`toggle-btn ${iconEditMode === 'links' ? 'active' : ''}`}
                            onClick={() => setIconEditMode('links')}
                          >
                            Links Page
                          </button>
                        </div>
                      </div>
                      
                      {/* Icon Content Mode */}
                      {iconEditMode === 'content' && renderIconContentEditor()}
                      
                      {/* Links Page Mode */}
                      {iconEditMode === 'links' && renderLinksPageEditor()}
                    </div>
                  ) : (
                    <div className="no-icon-selected">
                      <p>Select an icon from the list to edit its content.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-btn">Save Changes</button>
              <button type="button" className="cancel-btn" onClick={handleBackToList}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Render the modal outside the form to avoid form submission conflicts */}
      {renderEventFormModal()}
      {/* Render the Excel Import Modal */}
      {renderExcelImportModal()}
    </div>
  );
};

export default ConferenceManager;